
import React from 'react';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, Legend, BarChart, Bar, PieChart, Pie, Cell
} from 'recharts';
import { DollarSign, TrendingDown, Receipt, AlertCircle } from 'lucide-react';
import KpiDetailLayout from '@/components/kpi/KpiDetailLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

// Sample data for energy costs
const dailyCostData = [
  { date: '2023-06-01', cost: 182, baseline: 195 },
  { date: '2023-06-02', cost: 185, baseline: 198 },
  { date: '2023-06-03', cost: 190, baseline: 200 },
  { date: '2023-06-04', cost: 175, baseline: 190 },
  { date: '2023-06-05', cost: 200, baseline: 210 },
  { date: '2023-06-06', cost: 215, baseline: 220 },
  { date: '2023-06-07', cost: 225, baseline: 230 },
  { date: '2023-06-08', cost: 235, baseline: 240 },
  { date: '2023-06-09', cost: 245, baseline: 250 },
  { date: '2023-06-10', cost: 255, baseline: 260 },
  { date: '2023-06-11', cost: 250, baseline: 255 },
  { date: '2023-06-12', cost: 240, baseline: 250 },
  { date: '2023-06-13', cost: 230, baseline: 240 },
  { date: '2023-06-14', cost: 220, baseline: 230 },
  { date: '2023-06-15', cost: 210, baseline: 220 },
  { date: '2023-06-16', cost: 200, baseline: 210 },
  { date: '2023-06-17', cost: 195, baseline: 200 },
  { date: '2023-06-18', cost: 190, baseline: 195 },
  { date: '2023-06-19', cost: 180, baseline: 190 },
  { date: '2023-06-20', cost: 175, baseline: 185 },
  { date: '2023-06-21', cost: 170, baseline: 180 },
  { date: '2023-06-22', cost: 165, baseline: 175 },
  { date: '2023-06-23', cost: 160, baseline: 170 },
  { date: '2023-06-24', cost: 155, baseline: 165 },
  { date: '2023-06-25', cost: 150, baseline: 160 },
  { date: '2023-06-26', cost: 145, baseline: 155 },
  { date: '2023-06-27', cost: 140, baseline: 150 },
  { date: '2023-06-28', cost: 135, baseline: 145 },
  { date: '2023-06-29', cost: 130, baseline: 140 },
  { date: '2023-06-30', cost: 125, baseline: 135 },
];

const monthlyCostData = [
  { month: 'Jan', cost: 5800, baseline: 6200 },
  { month: 'Feb', cost: 5200, baseline: 5800 },
  { month: 'Mar', cost: 4800, baseline: 5200 },
  { month: 'Apr', cost: 4400, baseline: 4800 },
  { month: 'May', cost: 4600, baseline: 5000 },
  { month: 'Jun', cost: 4800, baseline: 5200 },
  { month: 'Jul', cost: 5200, baseline: 5600 },
  { month: 'Aug', cost: 5500, baseline: 5900 },
  { month: 'Sep', cost: 5200, baseline: 5600 },
  { month: 'Oct', cost: 4900, baseline: 5300 },
  { month: 'Nov', cost: 5100, baseline: 5500 },
  { month: 'Dec', cost: 5500, baseline: 5900 },
];

const costBreakdownData = [
  { name: 'Base Energy', value: 65 },
  { name: 'Demand Charges', value: 20 },
  { name: 'Taxes & Fees', value: 10 },
  { name: 'Surcharges', value: 5 },
];

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

// Overview tab content
const OverviewTab = () => (
  <div className="p-4 space-y-6">
    <div>
      <h3 className="text-lg font-medium mb-3">Daily Energy Cost Trend</h3>
      <div className="rounded-lg overflow-hidden border border-gray-200 dark:border-gray-800">
        <ResponsiveContainer width="100%" height={300}>
          <LineChart
            data={dailyCostData}
            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="date" 
              tick={{ fill: '#888', fontSize: 12 }}
              interval="preserveStartEnd"
            />
            <YAxis 
              tick={{ fill: '#888', fontSize: 12 }}
              label={{ value: 'Cost ($)', angle: -90, position: 'insideLeft' }}
            />
            <Tooltip formatter={(value) => [`$${value}`, 'Cost']} />
            <Legend />
            <Line
              type="monotone"
              dataKey="cost"
              stroke="#2563EB"
              strokeWidth={2}
              activeDot={{ r: 6 }}
              name="Actual Cost"
            />
            <Line
              type="monotone"
              dataKey="baseline"
              stroke="#94A3B8"
              strokeDasharray="5 5"
              strokeWidth={2}
              dot={false}
              name="Baseline Cost"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <p className="text-sm text-gray-500 mt-2">
        Daily energy cost with baseline comparison for the current month.
      </p>
    </div>
    
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card>
        <CardContent className="p-4">
          <h3 className="text-lg font-medium mb-3">Monthly Cost Comparison</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={monthlyCostData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip formatter={(value) => [`$${value}`, 'Cost']} />
              <Legend />
              <Bar dataKey="cost" name="Actual Cost" fill="#3B82F6" />
              <Bar dataKey="baseline" name="Baseline Cost" fill="#94A3B8" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
      
      <Card>
        <CardContent className="p-4">
          <h3 className="text-lg font-medium mb-3">Cost Breakdown</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={costBreakdownData}
                cx="50%"
                cy="50%"
                labelLine={true}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
                nameKey="name"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              >
                {costBreakdownData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => [`${value}%`, 'Percentage']} />
            </PieChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  </div>
);

// Savings tab content
const SavingsTab = () => (
  <div className="p-4 space-y-6">
    <div className="flex items-center gap-2 mb-4">
      <TrendingDown className="text-green-500 h-5 w-5" />
      <h3 className="text-lg font-medium">Cost Saving Opportunities</h3>
    </div>
    
    <Card>
      <CardContent className="p-4">
        <div className="space-y-4">
          <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-800 pb-3">
            <div>
              <p className="font-medium">Peak Demand Reduction</p>
              <p className="text-sm text-gray-500">Shifting loads to off-peak hours</p>
            </div>
            <Badge variant="secondary" className="text-md px-3 py-1">
              Save up to $320/mo
            </Badge>
          </div>
          
          <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-800 pb-3">
            <div>
              <p className="font-medium">HVAC Optimization</p>
              <p className="text-sm text-gray-500">Improved controls and scheduling</p>
            </div>
            <Badge variant="secondary" className="text-md px-3 py-1">
              Save up to $180/mo
            </Badge>
          </div>
          
          <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-800 pb-3">
            <div>
              <p className="font-medium">Lighting Retrofit</p>
              <p className="text-sm text-gray-500">LED upgrades and motion sensors</p>
            </div>
            <Badge variant="secondary" className="text-md px-3 py-1">
              Save up to $150/mo
            </Badge>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Equipment Maintenance</p>
              <p className="text-sm text-gray-500">Preventative maintenance schedule</p>
            </div>
            <Badge variant="secondary" className="text-md px-3 py-1">
              Save up to $90/mo
            </Badge>
          </div>
        </div>
      </CardContent>
    </Card>
    
    <p className="text-sm text-gray-500">
      Projected savings based on historical data and industry benchmarks.
    </p>
  </div>
);

// Billing tab content
const BillingTab = () => (
  <div className="p-4 space-y-6">
    <div className="flex items-center gap-2 mb-4">
      <Receipt className="text-blue-500 h-5 w-5" />
      <h3 className="text-lg font-medium">Billing Information</h3>
    </div>
    
    <Card>
      <CardContent className="p-4">
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-b border-gray-200 dark:border-gray-800 pb-3">
            <div>
              <p className="text-sm text-gray-500">Current Billing Period</p>
              <p className="font-medium">June 1 - June 30, 2023</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Current Charges</p>
              <p className="font-medium">$1,862.45</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-b border-gray-200 dark:border-gray-800 pb-3">
            <div>
              <p className="text-sm text-gray-500">Estimated Next Bill</p>
              <p className="font-medium">$1,720.10</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Payment Due Date</p>
              <p className="font-medium">July 15, 2023</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-b border-gray-200 dark:border-gray-800 pb-3">
            <div>
              <p className="text-sm text-gray-500">Average Rate</p>
              <p className="font-medium">$0.112 per kWh</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Demand Rate</p>
              <p className="font-medium">$14.50 per kW</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Billing Account</p>
              <p className="font-medium">BUS-10045-8772</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Rate Plan</p>
              <p className="font-medium">Commercial TOU-B</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
    
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <AlertCircle className="text-amber-500 h-5 w-5" />
          <h4 className="text-md font-medium">Billing Alert</h4>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Your May bill was 8.2% higher than the same period last year. This appears to be due to increased cooling demand and an electricity rate increase of 3.5%.
        </p>
      </CardContent>
    </Card>
  </div>
);

// Main Energy Cost component
const EnergyCost = () => {
  const tabs = [
    { id: 'overview', label: 'Overview', content: <OverviewTab /> },
    { id: 'savings', label: 'Cost Savings', content: <SavingsTab /> },
    { id: 'billing', label: 'Billing Details', content: <BillingTab /> }
  ];
  
  return (
    <KpiDetailLayout
      id="energy-cost"
      title="Energy Cost"
      icon={<DollarSign className="h-5 w-5 text-green-600" />}
      colorClass="bg-green-50 dark:bg-green-900/20"
      tabs={tabs}
    />
  );
};

export default EnergyCost;
